import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// Check if running in Capacitor native platform
const isNativePlatform = () => {
  try {
    return typeof window !== 'undefined' &&
           window.Capacitor &&
           window.Capacitor.isNativePlatform &&
           window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Native Google Sign-In Button Component
function NativeGoogleButton({ onSuccess, onError, text }) {
  const [loading, setLoading] = useState(false);
  const { googleLogin } = useAuth();

  const handleNativeGoogleSignIn = async () => {
    setLoading(true);
    try {
      // Access GoogleAuth plugin through Capacitor's registered plugins
      const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;

      if (!GoogleAuth) {
        throw new Error('Google Sign-In plugin not available');
      }

      // Initialize the plugin (reads clientId from GoogleService-Info.plist on iOS,
      // serverClientId from capacitor.config.ts — don't pass clientId here to avoid
      // overriding the platform-specific iOS client ID with the web client ID)
      await GoogleAuth.initialize({
        scopes: ['profile', 'email'],
        grantOfflineAccess: false
      });

      // Sign in using the native plugin
      const user = await GoogleAuth.signIn();

      // The idToken is what we send to our backend
      if (user.authentication?.idToken) {
        const result = await googleLogin(user.authentication.idToken);
        onSuccess?.(result);
      } else {
        throw new Error('No ID token received from Google');
      }
    } catch (err) {
      const msg = err.message || err.error || '';
      // Provide user-friendly messages for common native errors
      if (msg.includes('canceled') || msg.includes('cancelled') || msg === '12501') {
        // User cancelled — don't show error
        setLoading(false);
        return;
      } else if (msg.includes('network') || msg === '7') {
        onError?.('Network error. Please check your internet connection.');
      } else if (msg === '10' || msg.includes('DEVELOPER_ERROR')) {
        onError?.('Google Sign-In configuration error. Please contact support.');
      } else {
        onError?.(msg || 'Google Sign-In failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleNativeGoogleSignIn}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-surface-300 rounded-xl bg-white hover:bg-surface-50 transition-colors disabled:opacity-50"
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-surface-300 border-t-primary-600 rounded-full animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )}
      <span className="text-sm font-medium text-surface-700">
        {loading ? 'Signing in...' : (text === 'signup_with' ? 'Sign up with Google' : 'Sign in with Google')}
      </span>
    </button>
  );
}

// Web Google Sign-In Button Component
function WebGoogleButton({ onSuccess, onError, text }) {
  const buttonRef = useRef(null);
  const { googleLoaded, googleClientId, googleLogin } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  // Use refs to avoid re-creating callback on every parent render
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const handleCredentialResponse = useCallback(async (response) => {
    try {
      const result = await googleLogin(response.credential);
      onSuccessRef.current?.(result);
    } catch (err) {
      onErrorRef.current?.(err.message);
    }
  }, [googleLogin]);

  useEffect(() => {
    if (!googleClientId) {
      setError('Google Client ID not configured');
      return;
    }

    if (!googleLoaded) return;

    const checkGoogleSDK = () => {
      if (window.google?.accounts?.id) {
        setIsReady(true);
        setError(null);
        return true;
      }
      return false;
    };

    if (checkGoogleSDK()) return;

    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(() => {
      attempts++;
      if (checkGoogleSDK()) {
        clearInterval(checkInterval);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        setError('Google SDK failed to load. Check your internet connection.');
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [googleLoaded, googleClientId]);

  // Render Google button only once when ready — don't re-render on callback changes
  const hasRendered = useRef(false);
  useEffect(() => {
    if (!isReady || !googleClientId || !buttonRef.current) return;
    if (!window.google?.accounts?.id) return;
    if (hasRendered.current) return;

    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: text,
        shape: 'rectangular',
        logo_alignment: 'left',
        width: buttonRef.current.offsetWidth || 300
      });
      hasRendered.current = true;
    } catch (err) {
      setError('Failed to initialize Google Sign-In');
    }
  }, [isReady, googleClientId, handleCredentialResponse, text]);

  if (error) {
    return (
      <div className="w-full">
        <div className="w-full py-3 px-4 bg-red-50 border border-red-200 rounded-lg text-center">
          <span className="text-sm text-red-600">{error}</span>
        </div>
      </div>
    );
  }

  if (!isReady) return null;

  return (
    <div className="w-full h-[48px] flex items-center">
      <div ref={buttonRef} className="w-full flex justify-center" />
    </div>
  );
}

export default function GoogleLoginButton({ onSuccess, onError, text = 'signin_with' }) {
  const { googleClientId } = useAuth();

  // Don't render if no client ID configured
  if (!googleClientId) {
    return null;
  }

  // Use native button for native apps (Android + iOS), web button for browsers
  if (isNativePlatform()) {
    return <NativeGoogleButton onSuccess={onSuccess} onError={onError} text={text} />;
  }

  return <WebGoogleButton onSuccess={onSuccess} onError={onError} text={text} />;
}
